import Tokens from '@uipath/apollo-core';
import type { ErrorPageType } from '@uipath/portal-shell-types';
import type { IntlShape } from 'react-intl';

export const PROGRESS_MAX_WIDTH = 960;

export const roundPct = (n: number) => Math.round(n * 10) / 10;

// Maps an SWR/axios error to an Apollo error-page type (defaults to 500).
export const getErrorStatusCode = (error: unknown): ErrorPageType => {
    if (error && typeof error === 'object' && 'status' in error) {
        const status = (error as { status?: unknown }).status;
        if (typeof status === 'number' || typeof status === 'string') {
            return String(status) as ErrorPageType;
        }
    }
    return '500';
};

const remainingMs = (value?: string | number | null): number | undefined => {
    if (value == null || value === '') {
        return undefined;
    }
    const ms = typeof value === 'number' ? value * 1000 : new Date(value).getTime();
    if (Number.isNaN(ms)) {
        return undefined;
    }
    return Math.max(0, ms - Date.now());
};

// "Resets in N hours" when the next refresh is under a day away (e.g. a daily pacing
// reset), otherwise "Resets in N days".
export const formatResets = (
    translate: IntlShape['formatMessage'],
    value?: string | number | null,
): string | undefined => {
    const ms = remainingMs(value);
    if (ms === undefined) {
        return undefined;
    }
    if (ms < 86_400_000) {
        return translate(
            { id: 'CLIENT_USAGE_RESETS_IN_HOURS' },
            { 0: Math.max(1, Math.ceil(ms / 3_600_000)) },
        ) as string;
    }
    return translate(
        { id: 'CLIENT_USAGE_RESETS_IN_DAYS' },
        { 0: Math.ceil(ms / 86_400_000) },
    ) as string;
};

const SERVICE_COLOR: Record<string, string> = {
    Delegate: Tokens.Colors.ColorChartBlueSecondaryDark,
    Autopilot: Tokens.Colors.ColorChartGreenDark,
    AgentDevelopment: Tokens.Colors.ColorChartPurpleDark,
    ConversationalAgents: Tokens.Colors.ColorChartLightBlueDark,
    AutopilotForEveryone: Tokens.Colors.ColorChartPinkDark,
    Screenplay: Tokens.Colors.ColorChartYellowDark,
    CodingAgent: Tokens.Colors.ColorBlueSecondary600,
    HealingAgent: Tokens.Colors.ColorGreen750,
    UIAutomation: Tokens.Colors.ColorLightBlue800,
    ServerlessExecution: Tokens.Colors.ColorYellow800,

    // Shares AutopilotForEveryone's colour on purpose: the per-user view shows that product and never
    // Context Grounding, and the organization view the reverse, so the two cannot meet in one bar. Give
    // Context Grounding its own colour if a pool is ever configured to hold both.
    ContextGrounding: Tokens.Colors.ColorChartPinkDark,

    IntegrationService: Tokens.Colors.ColorLightBlue700,

    Other: Tokens.Colors.ColorGray550,
};

const UNASSIGNED_COLORS = [
    Tokens.Colors.ColorInk550,
    Tokens.Colors.ColorGray400,
    Tokens.Colors.ColorPink200,
    Tokens.Colors.ColorYellow750,
];

const hashCode = (value: string) => {
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
        hash = ((hash << 5) - hash + value.charCodeAt(i)) | 0;
    }
    return Math.abs(hash);
};

export const serviceColor = (code: string): string =>
    SERVICE_COLOR[code] ?? UNASSIGNED_COLORS[hashCode(code) % UNASSIGNED_COLORS.length];

export const serviceName = (translate: IntlShape['formatMessage'], code: string): string =>
    translate({
        id: `CLIENT_USAGE_SERVICE_${code}`,
        defaultMessage: code,
    }) as string;
